<template>
  <IntercomModal
    v-model:show="showModal"
    title="Keyboard Shortcuts"
    size="lg"
    :close-on-overlay="true"
    :close-on-escape="true"
  >
    <div class="keyboard-help">
      <!-- Navigation -->
      <div class="shortcut-section">
        <h3 class="shortcut-section-title">Navigation</h3>
        <div class="shortcut-grid">
          <div class="shortcut-item" v-for="item in navigationShortcuts" :key="item.keys">
            <kbd class="shortcut-key" v-for="k in item.keys.split(' ')" :key="k">{{ k }}</kbd>
            <span class="shortcut-desc">{{ item.description }}</span>
          </div>
        </div>
      </div>

      <!-- List Views -->
      <div class="shortcut-section">
        <h3 class="shortcut-section-title">List Views</h3>
        <div class="shortcut-grid">
          <div class="shortcut-item" v-for="item in listShortcuts" :key="item.keys">
            <kbd class="shortcut-key" v-for="k in item.keys.split(' ')" :key="k">{{ k }}</kbd>
            <span class="shortcut-desc">{{ item.description }}</span>
          </div>
        </div>
      </div>

      <!-- Forms -->
      <div class="shortcut-section">
        <h3 class="shortcut-section-title">Forms (inside modals)</h3>
        <div class="shortcut-grid">
          <div class="shortcut-item" v-for="item in formShortcuts" :key="item.keys">
            <kbd class="shortcut-key" v-for="k in item.keys.split(' ')" :key="k">{{ k }}</kbd>
            <span class="shortcut-desc">{{ item.description }}</span>
          </div>
        </div>
      </div>

      <!-- General -->
      <div class="shortcut-section">
        <h3 class="shortcut-section-title">General</h3>
        <div class="shortcut-grid">
          <div class="shortcut-item" v-for="item in generalShortcuts" :key="item.keys">
            <kbd class="shortcut-key" v-for="k in item.keys.split(' ')" :key="k">{{ k }}</kbd>
            <span class="shortcut-desc">{{ item.description }}</span>
          </div>
        </div>
      </div>
    </div>
  </IntercomModal>
</template>

<script setup>
import { computed } from 'vue'
import IntercomModal from './IntercomModal.vue'

const props = defineProps({
  show: Boolean,
})

const emit = defineEmits(['update:show'])

const showModal = computed({
  get: () => props.show,
  set: (val) => emit('update:show', val),
})

const navigationShortcuts = [
  { keys: 'G D', description: 'Go to Dashboard' },
  { keys: 'G I', description: 'Go to Invoices' },
  { keys: 'G P', description: 'Go to Products' },
  { keys: 'G Q', description: 'Go to Quotations' },
  { keys: 'G C', description: 'Go to Customers' },
  { keys: 'G V', description: 'Go to Vendors' },
  { keys: 'G B', description: 'Go to Purchase Bills' },
  { keys: 'G R', description: 'Go to Reports' },
  { keys: 'G S', description: 'Go to Settings' },
  { keys: 'G O', description: 'Go to POS' },
]

const listShortcuts = [
  { keys: 'N', description: 'Create new item' },
  { keys: '/', description: 'Focus search bar' },
  { keys: 'J', description: 'Select next row' },
  { keys: 'K', description: 'Select previous row' },
  { keys: 'Enter', description: 'Open selected row' },
  { keys: 'E', description: 'Edit selected row' },
  { keys: 'Delete', description: 'Delete selected row' },
  { keys: 'Esc', description: 'Clear selection / blur search' },
]

const formShortcuts = [
  { keys: 'Enter', description: 'Advance to next field' },
  { keys: 'Esc', description: 'Close dropdown / close modal' },
  { keys: 'Tab', description: 'Advance from textareas' },
  { keys: '\u2190', description: 'Previous field in row (at cursor start)' },
  { keys: '\u2192', description: 'Next field in row (at cursor end)' },
]

const generalShortcuts = [
  { keys: '?', description: 'Show this help' },
  { keys: 'Esc', description: 'Blur active element / dismiss' },
]
</script>

<style scoped>
.keyboard-help {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.shortcut-section-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: var(--space-3);
}

.shortcut-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-2) var(--space-8);
}

.shortcut-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) 0;
}

.shortcut-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 var(--space-2);
  background: var(--color-gray-100);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-family: var(--font-mono);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-700);
}

.shortcut-desc {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
}

@media (max-width: 768px) {
  .shortcut-grid {
    grid-template-columns: 1fr;
  }
}
</style>
